import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { createGeminiResult, getJobsStatus, queryGeminiSummaryResult } from '@/api/subtitles';
import { stripMarkdownDividers } from '@/utils/common';
import './GeminiContent.less';
import MeetStatus, { IMeetStatus } from './MeetStatus';
import Test from './test';

// 定义生成类型枚举
export enum GeminiGenerateType {
  SUMMARY = 'SUMMARY_PROMPT',
  TRANSCRIPTION = 'TRANSCRIPTION_PROMPT',
  TRANSLATE = 'TRANSLATE_PROMPT',
}

interface GeminiContentProps {
  isFinish: boolean;
  meetStatus: IMeetStatus;
  fileMergeId: string;
  generateType: GeminiGenerateType;
  targetLang: string;
  handleEndMeeting: () => void;
  onSummaryEmpty?: () => void;
}

const GeminiContent: React.FC<GeminiContentProps> = ({
  isFinish,
  meetStatus,
  fileMergeId,
  targetLang,
  handleEndMeeting,
  onSummaryEmpty,
}) => {
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadMsg, setLoadMsg] = useState(t('meeting.queryingSummary'));
  const [content, setContent] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  const fetchGeminiResult = useCallback(async () => {
    try {
      setLoading(true);
      const {
        data: { data: jobsStatus },
      } = await getJobsStatus({
        file_merge_id: fileMergeId,
        prompt_type: 'SUMMARY_PROMPT',
      });

      if (jobsStatus.generate_job_details.status === 'INIT') {
        setLoadMsg(t('meeting.summaryGenerating'));
        return;
      }

      if (jobsStatus.generate_job_details.status !== 'FINISH') {
        return;
      }

      clearInterval(timerRef.current);

      const {
        data: {
          data: { summary_text },
        },
      } = await queryGeminiSummaryResult({
        file_merge_id: fileMergeId,
      });

      if (!summary_text) {
        const {
          data: {
            data: { multimodal_summary_details: createResult },
          },
        } = await createGeminiResult({
          file_merge_id: fileMergeId,
          prompt_type: 'SUMMARY_PROMPT',
          target_lang: targetLang,
        });

        if (createResult) {
          setTimeout(fetchGeminiResult, 500);
        }
      } else {
        setContent(stripMarkdownDividers(summary_text));
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch Gemini result:', error);
      setErrorMsg(t('meeting.getSummaryFailed'));
      onSummaryEmpty?.();
    }
  }, [fileMergeId, targetLang, t, onSummaryEmpty]);

  useEffect(() => {
    if (isFinish && fileMergeId) {
      timerRef.current = setInterval(fetchGeminiResult, 3000);
      fetchGeminiResult();
    }

    return () => {
      setContent(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        setLoading(false);
      }
    };
  }, [fileMergeId, isFinish, fetchGeminiResult]);

  if (meetStatus !== 'FINISH') {
    return <MeetStatus meetStatus={meetStatus} handleEndMeeting={handleEndMeeting} contentType="summary" />;
  }

  if (errorMsg) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text error-text">{errorMsg}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="gemini-content">
      {loading ? (
        <div className="loading-container">
          <div className="loading-text">
            <LoadingOutlined />
            {loadMsg}
          </div>
        </div>
      ) : (
        <div className="content-container">
          <div className="markdown-content-title">{t('meeting.meetingSummary')}</div>
          <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]}>
            {content || ''}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default GeminiContent;
